import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  CheckCircle, Target, Eye, Lightbulb, Users, TrendingUp,
  Shield, ArrowRight, Star, Package, Sprout
} from 'lucide-react';

import CompanyImg from '../assets/image/Company.jpg';

/* ─── original data — untouched ─────────────────────────────── */
const whyUsPoints = [
  { icon: Shield,      title: 'Quality You Can Trust',           desc: 'We partner with WHO-GMP certified manufacturing units to ensure every product meets stringent quality, safety, and efficacy standards.' },
  { icon: Package,     title: 'Extensive Product Portfolio',     desc: 'From general medicines to specialty segments, we offer a wide and continuously expanding range of pharmaceutical products.' },
  { icon: TrendingUp,  title: 'Strong Marketing & Distribution', desc: 'Our well-established distribution channels and strategic marketing approach ensure timely availability and strong brand presence.' },
  { icon: CheckCircle, title: 'Regulatory & Compliance Excellence', desc: 'We strictly adhere to all pharmaceutical regulations and guidelines, ensuring smooth operations and reliable product approvals.' },
  { icon: Users,       title: 'Customer-Centric Approach',       desc: 'We believe in building lasting relationships with dedicated support, transparent communication, and customized solutions.' },
  { icon: Star,        title: 'Competitive Pricing',             desc: 'We offer premium-quality products at competitive prices, enabling our partners to maximize profitability without compromising standards.' },
  { icon: Lightbulb,   title: 'Innovative & Growth-Oriented',   desc: 'We continuously upgrade our portfolio and marketing strategies to stay ahead in a competitive pharmaceutical landscape.' },
  { icon: Target,      title: 'Ethical Business Practices',     desc: 'Integrity and transparency are the foundation of our business. We follow ethical marketing practices aligned with industry norms.' },
];

const milestones = [
  { year: '2010', event: 'Company founded with vision to provide quality healthcare for all' },
  { year: '2013', event: 'Expanded portfolio to 100+ pharmaceutical products' },
  { year: '2016', event: 'Launched own brand nutraceuticals division' },
  { year: '2019', event: 'Established cosmetics and personal care product line' },
  { year: '2022', event: 'Launched surgical and medical equipment division' },
  { year: '2024', event: '500+ products serving 20+ states across India' },
];

/* ─── CSS ────────────────────────────────────────────────────── */
const css = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}

:root{
  --t:#2eb4b4;
  --n:#174f9a;
  --grad:linear-gradient(135deg,#2eb4b4 0%,#174f9a 100%);
  --grad-r:linear-gradient(135deg,#174f9a 0%,#2eb4b4 100%);
  --t15:rgba(46,180,180,.15);
  --n15:rgba(23,79,154,.15);
  --white:rgba(255,255,255,1);
  --off:#f4f8fa;
  --card:#fff;
  --border:#dde8f0;
  --text:#0d1421;
  --text2:#4a5872;
  --text3:#8492a6;
  scroll-behavior:smooth;
}

/* ── Reveal ── */
.rv{opacity:0;transform:translateY(36px);transition:opacity .75s cubic-bezier(.16,1,.3,1),transform .75s cubic-bezier(.16,1,.3,1);}
.rv.in{opacity:1;transform:none;}
.rv.d1{transition-delay:.08s}.rv.d2{transition-delay:.16s}.rv.d3{transition-delay:.24s}
.rv.d4{transition-delay:.32s}.rv.d5{transition-delay:.40s}.rv.d6{transition-delay:.48s}

/* ── Floating particles canvas ── */
#ptcl{position:absolute;inset:0;pointer-events:none;z-index:1;}

/* ═══════════════ HERO ═══════════════ */
.hero{
  position:relative;min-height:100vh;display:flex;flex-direction:column;
  align-items:center;justify-content:center;text-align:center;
  background:linear-gradient(145deg,#0b2a5c 0%,#174f9a 40%,#1a8fa0 75%,#2eb4b4 100%);
  overflow:hidden;padding:6rem 1.25rem 5rem;
}

/* animated mesh overlay */
.hero-mesh{
  position:absolute;inset:0;z-index:0;pointer-events:none;
  background:
    radial-gradient(ellipse 70% 55% at 15% 30%,rgba(255,255,255,.08) 0%,transparent 65%),
    radial-gradient(ellipse 50% 70% at 85% 75%,rgba(46,180,180,.18) 0%,transparent 60%),
    radial-gradient(ellipse 40% 40% at 60% 10%,rgba(255,255,255,.05) 0%,transparent 55%);
}

/* blobs */
.blob{
  position:absolute;border-radius:50%;filter:blur(60px);pointer-events:none;z-index:0;
  animation:blobDrift 18s ease-in-out infinite;
}
.blob1{width:520px;height:520px;background:rgba(46,180,180,.22);top:-120px;left:-160px;animation-delay:0s;}
.blob2{width:400px;height:400px;background:rgba(23,79,154,.28);bottom:-80px;right:-100px;animation-delay:-7s;}
.blob3{width:300px;height:300px;background:rgba(46,180,180,.15);top:40%;left:55%;animation-delay:-12s;}
@keyframes blobDrift{
  0%,100%{transform:translate(0,0) scale(1);}
  33%{transform:translate(30px,-25px) scale(1.06);}
  66%{transform:translate(-20px,20px) scale(.95);}
}

/* hero rings */
.h-rings{position:absolute;inset:0;z-index:1;pointer-events:none;}
.h-ring{
  position:absolute;border-radius:50%;border:1px solid rgba(255,255,255,.07);
  animation:hRingPulse ease-in-out infinite;
}
@keyframes hRingPulse{0%,100%{transform:translate(-50%,-50%) scale(1);opacity:.7;}50%{transform:translate(-50%,-50%) scale(1.05);opacity:.25;}}

/* pill */
.h-pill{
  position:relative;z-index:3;
  display:inline-flex;align-items:center;gap:.4rem;
  background:rgba(255,255,255,.12);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
  border:1px solid rgba(255,255,255,.22);border-radius:99px;
  color:rgba(255,255,255,.92);font-size:.78rem;font-weight:600;letter-spacing:.1em;text-transform:uppercase;
  padding:.42rem 1.1rem;margin-bottom:2rem;
  animation:pillIn .8s cubic-bezier(.16,1,.3,1) both;
}
@keyframes pillIn{from{opacity:0;transform:translateY(-16px);}to{opacity:1;transform:none;}}

.hero h1{
  position:relative;z-index:3;
  font-size:clamp(2.4rem,6.5vw,5.2rem);font-weight:900;line-height:1.04;
  color:#fff;letter-spacing:-.025em;margin-bottom:1.4rem;
  animation:heroTitleIn 1s cubic-bezier(.16,1,.3,1) .15s both;
}
@keyframes heroTitleIn{from{opacity:0;transform:translateY(24px);}to{opacity:1;transform:none;}}

.hero h1 .grad-word{
  background:linear-gradient(100deg,#aff4f4,#c8e2ff,#aff4f4);
  background-size:200%;
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
  animation:shimmer 4s linear 1.2s infinite;
}
@keyframes shimmer{0%{background-position:0%}100%{background-position:200%}}

.hero p{
  position:relative;z-index:3;
  font-size:clamp(.95rem,2vw,1.15rem);color:rgba(255,255,255,.72);max-width:560px;line-height:1.75;
  margin-bottom:2.8rem;
  animation:heroTitleIn 1s cubic-bezier(.16,1,.3,1) .3s both;
}

/* floating pill badges in hero */
.hero-badges{
  position:relative;z-index:3;display:flex;flex-wrap:wrap;gap:.6rem;justify-content:center;
  margin-bottom:2.8rem;
  animation:heroTitleIn 1s cubic-bezier(.16,1,.3,1) .45s both;
}
.h-badge{
  display:inline-flex;align-items:center;gap:.35rem;
  background:rgba(255,255,255,.1);backdrop-filter:blur(8px);
  border:1px solid rgba(255,255,255,.18);border-radius:99px;
  color:rgba(255,255,255,.88);font-size:.75rem;font-weight:500;
  padding:.3rem .85rem;
}
.h-badge svg{width:13px;height:13px;stroke:currentColor;fill:none;stroke-width:2;flex-shrink:0;}

.hero-cta{
  position:relative;z-index:3;
  display:inline-flex;align-items:center;gap:.55rem;
  background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.32);
  color:#fff;padding:.85rem 2.1rem;border-radius:99px;
  font-weight:600;font-size:.95rem;text-decoration:none;cursor:pointer;
  transition:background .3s,transform .25s,box-shadow .3s;
  animation:heroTitleIn 1s cubic-bezier(.16,1,.3,1) .6s both;
  box-shadow:0 0 0 0 rgba(46,180,180,0);
}
.hero-cta:hover{background:rgba(255,255,255,.25);transform:translateY(-3px);box-shadow:0 0 28px rgba(46,180,180,.45);}

.hero-scroll{
  position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);z-index:3;
  color:rgba(255,255,255,.45);display:flex;flex-direction:column;align-items:center;gap:.25rem;
  font-size:.7rem;letter-spacing:.08em;text-transform:uppercase;
  animation:scrollBounce 2.5s ease-in-out infinite;
}
@keyframes scrollBounce{0%,100%{transform:translateX(-50%) translateY(0);}50%{transform:translateX(-50%) translateY(-7px);}}
.hero-scroll-line{width:1px;height:36px;background:linear-gradient(to bottom,rgba(255,255,255,.4),transparent);}

/* ═══════════════ STATS ═══════════════ */
.stats-wrap{
  background:#fff;border-bottom:1px solid var(--border);
  padding:0 1.25rem;
  box-shadow:0 4px 24px rgba(23,79,154,.06);
}
.stats-inner{
  max-width:1100px;margin:0 auto;
  display:grid;grid-template-columns:repeat(4,1fr);
}
@media(max-width:640px){.stats-inner{grid-template-columns:repeat(2,1fr);}}
.stat-cell{
  padding:1.8rem 1rem;text-align:center;position:relative;
  border-right:1px solid var(--border);
}
.stat-cell:last-child{border-right:none;}
@media(max-width:640px){
  .stat-cell:nth-child(2){border-right:none;}
  .stat-cell:nth-child(3){border-top:1px solid var(--border);}
  .stat-cell:nth-child(4){border-top:1px solid var(--border);border-right:none;}
}
.stat-num{
  font-size:2.2rem;font-weight:900;line-height:1;
  background:var(--grad);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
}
.stat-label{font-size:.72rem;font-weight:600;color:var(--text3);letter-spacing:.08em;text-transform:uppercase;margin-top:.35rem;}
.stat-cell::after{
  content:'';position:absolute;bottom:0;left:20%;right:20%;height:2px;
  background:var(--grad);border-radius:1px;transform:scaleX(0);transition:transform .4s cubic-bezier(.16,1,.3,1);
}
.stat-cell:hover::after{transform:scaleX(1);}

/* ═══════════════ SECTIONS ═══════════════ */
.sec{padding:6rem 1.25rem;position:relative;overflow:hidden;}
.sec-in{max-width:1100px;margin:0 auto;position:relative;z-index:2;}
.sec-tag{
  display:inline-block;font-size:.7rem;font-weight:700;letter-spacing:.12em;text-transform:uppercase;
  color:var(--t);margin-bottom:.7rem;
}
.sec-title{font-size:clamp(1.8rem,3.8vw,2.75rem);font-weight:900;letter-spacing:-.02em;line-height:1.12;color:var(--text);}
.sec-sub{font-size:.98rem;color:var(--text2);line-height:1.75;margin-top:.75rem;}

/* ── decorative bg shapes in sections ── */
.deco-circle{
  position:absolute;border-radius:50%;pointer-events:none;z-index:0;
  background:radial-gradient(circle,rgba(46,180,180,.07) 0%,transparent 70%);
}
.deco-circle.tl{width:600px;height:600px;top:-200px;left:-200px;}
.deco-circle.br{width:500px;height:500px;bottom:-150px;right:-150px;}
.deco-grid{
  position:absolute;inset:0;z-index:0;pointer-events:none;
  background-image:
    linear-gradient(rgba(46,180,180,.04) 1px,transparent 1px),
    linear-gradient(90deg,rgba(46,180,180,.04) 1px,transparent 1px);
  background-size:48px 48px;
}

/* ═══════════════ ABOUT ═══════════════ */
.about-bg{background:#fff;}
.about-grid{display:grid;grid-template-columns:1fr 1fr;gap:4.5rem;align-items:center;}
@media(max-width:860px){.about-grid{grid-template-columns:1fr;gap:2.5rem;}}

.about-text p{font-size:.98rem;color:var(--text2);line-height:1.82;margin-bottom:1rem;}

.vm-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-top:1.75rem;}
@media(max-width:420px){.vm-grid{grid-template-columns:1fr;}}
.vm-card{
  padding:1.2rem 1.3rem;border-radius:16px;
  background:linear-gradient(135deg,rgba(46,180,180,.06) 0%,rgba(23,79,154,.06) 100%);
  border:1px solid rgba(46,180,180,.2);
  transition:transform .3s,box-shadow .3s;
}
.vm-card:hover{transform:translateY(-3px);box-shadow:0 12px 32px rgba(46,180,180,.12);}
.vm-icon-wrap{
  width:38px;height:38px;border-radius:10px;background:var(--grad);
  display:flex;align-items:center;justify-content:center;margin-bottom:.7rem;
  box-shadow:0 4px 12px rgba(46,180,180,.35);
}
.vm-card h4{font-size:.9rem;font-weight:700;color:var(--text);margin-bottom:.35rem;}
.vm-card p{font-size:.8rem;color:var(--text2);line-height:1.6;}

/* GHC card */
.ghc-card{
  border-radius:22px;background:var(--grad);padding:2.4rem 2.2rem;color:#fff;
  position:relative;overflow:hidden;
  box-shadow:0 24px 70px rgba(23,79,154,.32),0 0 0 1px rgba(255,255,255,.08) inset;
}
.ghc-card::before{
  content:'GHC';position:absolute;top:-15px;right:-15px;
  font-size:10rem;font-weight:900;color:rgba(255,255,255,.05);line-height:1;
  pointer-events:none;letter-spacing:-.04em;
}
.ghc-card::after{
  content:'';position:absolute;
  width:300px;height:300px;border-radius:50%;
  background:radial-gradient(circle,rgba(255,255,255,.07),transparent 70%);
  bottom:-80px;left:-60px;pointer-events:none;
}
.ghc-card h3{font-size:1.55rem;font-weight:900;margin-bottom:1.4rem;letter-spacing:-.01em;}
.ghc-row{
  display:flex;align-items:center;gap:.75rem;padding:.65rem 0;
  border-bottom:1px solid rgba(255,255,255,.1);
  font-size:.88rem;color:rgba(255,255,255,.87);font-weight:500;
  transition:color .2s;
}
.ghc-row:hover{color:#fff;}
.ghc-row:last-child{border-bottom:none;}
.ghc-dot{
  width:22px;height:22px;border-radius:50%;background:rgba(255,255,255,.18);flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  transition:background .2s;
}
.ghc-row:hover .ghc-dot{background:rgba(255,255,255,.3);}

/* ═══════════════ MILESTONES ═══════════════ */
.milestones-bg{background:var(--off);}
.tl-wrap{position:relative;max-width:760px;margin:0 auto;padding:0 .5rem;}

/* vertical spine — desktop centred, mobile left-anchored */
.tl-spine{
  position:absolute;top:0;bottom:0;width:2px;
  background:linear-gradient(to bottom,transparent 0%,var(--t) 6%,var(--n) 94%,transparent 100%);
  /* desktop: centre */
  left:50%;transform:translateX(-50%);
}
@media(max-width:640px){
  .tl-spine{left:24px;transform:none;}
}

/* ── desktop alternating row ── */
.tl-row{
  display:grid;
  grid-template-columns:1fr 60px 1fr;
  align-items:center;
  gap:0;
  margin-bottom:2.6rem;
}
.tl-row:last-child{margin-bottom:0;}

/* on mobile collapse to single left-aligned column */
@media(max-width:640px){
  .tl-row{
    display:flex;
    flex-direction:row;
    align-items:flex-start;
    gap:0;
    padding-left:0;
    margin-bottom:2rem;
  }
}

/* dot */
.tl-dot{
  width:52px;height:52px;border-radius:50%;background:var(--grad);
  display:flex;align-items:center;justify-content:center;
  font-size:.65rem;font-weight:800;color:#fff;text-align:center;line-height:1.25;
  flex-shrink:0;
  justify-self:center;
  position:relative;z-index:2;
  box-shadow:0 0 0 5px rgba(46,180,180,.14),0 4px 18px rgba(23,79,154,.28);
  transition:transform .3s,box-shadow .3s;
}
.tl-row:hover .tl-dot{
  transform:scale(1.1);
  box-shadow:0 0 0 9px rgba(46,180,180,.16),0 6px 24px rgba(23,79,154,.35);
}
@media(max-width:640px){
  .tl-dot{
    width:44px;height:44px;font-size:.6rem;
    flex-shrink:0;margin-right:0;
    /* keep dot aligned with spine centre = 24px left → dot is 44px so needs 2px offset */
    margin-left:2px;
  }
}

/* card */
.tl-card{
  background:var(--card);border:1px solid var(--border);border-radius:14px;
  padding:.9rem 1.2rem;position:relative;
  box-shadow:0 2px 14px rgba(0,0,0,.04);
  transition:box-shadow .35s,transform .35s,border-color .35s;
}
.tl-card:hover{
  box-shadow:0 10px 32px rgba(46,180,180,.15);
  transform:translateY(-3px);
  border-color:rgba(46,180,180,.32);
}
.tl-card p{font-size:.87rem;color:var(--text2);line-height:1.65;}

/* arrow nub — desktop only */
.tl-row.tl-left  .tl-card{grid-column:1;}
.tl-row.tl-right .tl-card{grid-column:3;}

.tl-row.tl-left  .tl-card::after,
.tl-row.tl-right .tl-card::after{
  content:'';position:absolute;top:50%;
  width:10px;height:10px;
  background:var(--card);border:1px solid var(--border);
  transform:translateY(-50%) rotate(45deg);
}
.tl-row.tl-left  .tl-card::after{right:-5px;border-left:none;border-bottom:none;}
.tl-row.tl-right .tl-card::after{left:-5px; border-right:none;border-top:none;}

/* hide nubs on mobile, use left margin for spacing instead */
@media(max-width:640px){
  .tl-card{margin-left:14px;flex:1;}
  .tl-row.tl-left  .tl-card::after,
  .tl-row.tl-right .tl-card::after{display:none;}
}

/* empty spacer cell desktop */
.tl-spacer{display:block;}
@media(max-width:640px){.tl-spacer{display:none;}}

/* ═══════════════ WHY US ═══════════════ */
.why-bg{background:#fff;}
.why-grid{
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(235px,1fr));
  gap:1.1rem;margin-top:3rem;
}
.why-card{
  padding:1.65rem;border-radius:18px;border:1px solid var(--border);
  background:var(--card);cursor:default;position:relative;overflow:hidden;
  transition:box-shadow .35s,border-color .35s,transform .35s;
}
.why-card::before{
  content:'';position:absolute;inset:0;border-radius:18px;
  background:linear-gradient(135deg,rgba(46,180,180,.06) 0%,rgba(23,79,154,.06) 100%);
  opacity:0;transition:opacity .35s;
}
.why-card:hover{box-shadow:0 14px 44px rgba(46,180,180,.14);border-color:rgba(46,180,180,.35);transform:translateY(-5px);}
.why-card:hover::before{opacity:1;}
.why-card:hover .w-icon{transform:scale(1.12) rotate(-6deg);}
.w-icon{
  width:48px;height:48px;border-radius:13px;background:var(--grad);
  display:flex;align-items:center;justify-content:center;margin-bottom:1.1rem;
  box-shadow:0 6px 18px rgba(46,180,180,.32);
  transition:transform .35s cubic-bezier(.34,1.56,.64,1);
}
.why-card h3{font-size:.93rem;font-weight:700;color:var(--text);margin-bottom:.5rem;}
.why-card p{font-size:.8rem;color:var(--text2);line-height:1.66;}

/* glow accent on hover */
.why-card::after{
  content:'';position:absolute;width:120px;height:120px;border-radius:50%;
  background:radial-gradient(circle,rgba(46,180,180,.18),transparent 70%);
  top:-30px;right:-30px;pointer-events:none;
  transition:transform .5s;
}
.why-card:hover::after{transform:scale(1.6);}

/* ═══════════════ INNOVATION ═══════════════ */
.innov-bg{
  background:linear-gradient(155deg,#060f1e 0%,#0c1e3d 50%,#0a2a2a 100%);
  position:relative;overflow:hidden;
}
.innov-dots{
  position:absolute;inset:0;pointer-events:none;z-index:0;
  background-image:radial-gradient(rgba(46,180,180,.1) 1px,transparent 1px);
  background-size:36px 36px;
}
.innov-blob1{
  position:absolute;width:500px;height:500px;border-radius:50%;
  background:radial-gradient(circle,rgba(46,180,180,.1) 0%,transparent 70%);
  top:-150px;right:-100px;pointer-events:none;
  animation:blobDrift 20s ease-in-out infinite;
}
.innov-blob2{
  position:absolute;width:350px;height:350px;border-radius:50%;
  background:radial-gradient(circle,rgba(23,79,154,.12) 0%,transparent 70%);
  bottom:-80px;left:-80px;pointer-events:none;
  animation:blobDrift 16s ease-in-out -8s infinite;
}

.innov-grid{display:grid;grid-template-columns:1fr 1fr;gap:5rem;align-items:center;}
@media(max-width:860px){.innov-grid{grid-template-columns:1fr;gap:3rem;}}

.innov-tag{color:rgba(46,180,180,.85);}
.innov-title{color:#fff;}
.innov-body{font-size:.97rem;color:rgba(255,255,255,.6);line-height:1.8;margin-bottom:1rem;}

.innov-feat{
  display:flex;gap:1rem;padding:1.15rem 1.3rem;border-radius:14px;
  background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);
  margin-bottom:.8rem;transition:background .3s,border-color .3s,transform .3s;
}
.innov-feat:last-child{margin-bottom:0;}
.innov-feat:hover{background:rgba(46,180,180,.08);border-color:rgba(46,180,180,.25);transform:translateX(4px);}
.i-dot{width:8px;height:8px;background:var(--t);border-radius:50%;flex-shrink:0;margin-top:.45rem;}
.innov-feat h4{font-size:.9rem;font-weight:700;color:#fff;margin-bottom:.28rem;}
.innov-feat p{font-size:.79rem;color:rgba(255,255,255,.5);line-height:1.6;}

/* ── Banyan orb ── */
.orb-wrap{position:relative;width:320px;height:320px;margin:0 auto;flex-shrink:0;}
@media(max-width:480px){.orb-wrap{width:230px;height:230px;}}
.orb-ring{
  position:absolute;border-radius:50%;border:1px solid;
  top:50%;left:50%;
  transform:translate(-50%,-50%);
  animation:orbSpin linear infinite;
}
@keyframes orbSpin{from{transform:translate(-50%,-50%) rotate(0deg);}to{transform:translate(-50%,-50%) rotate(360deg);}}
.orb-glow{
  position:absolute;inset:70px;border-radius:50%;
  background:radial-gradient(circle,rgba(46,180,180,.25) 0%,transparent 70%);
  animation:orbGlowPulse 3s ease-in-out infinite;
}
@keyframes orbGlowPulse{0%,100%{opacity:.6;transform:scale(1);}50%{opacity:1;transform:scale(1.1);}}
.orb-center{
  position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
  flex-direction:column;text-align:center;z-index:2;
}

/* ═══════════════ GLOBAL RESPONSIVE ═══════════════ */
@media(max-width:1024px){
  .sec{padding:5rem 1.25rem;}
  .about-grid{gap:3rem;}
  .innov-grid{gap:3rem;}
}
@media(max-width:768px){
  .sec{padding:4rem 1.1rem;}
  .ghc-card{padding:1.8rem 1.5rem;}
  .stats-inner{grid-template-columns:repeat(2,1fr);}
  .stat-cell:nth-child(2){border-right:none;}
  .stat-cell:nth-child(3){border-top:1px solid var(--border);}
  .stat-cell:nth-child(4){border-top:1px solid var(--border);border-right:none;}
  .why-grid{grid-template-columns:repeat(2,1fr);}
  .innov-grid{grid-template-columns:1fr;}
  .orb-wrap{width:260px;height:260px;}
}
@media(max-width:640px){
  .about-grid{grid-template-columns:1fr;gap:2rem;}
  .vm-grid{grid-template-columns:1fr 1fr;}
  /* timeline mobile: single column, dot left-aligned with spine */
  .tl-row{
    display:flex !important;
    flex-direction:row !important;
    align-items:flex-start !important;
    padding-left:0 !important;
  }
  .tl-dot{
    margin-left:2px;
    flex-shrink:0;
  }
}
@media(max-width:480px){
  .sec{padding:3rem .85rem;}
  .hero{padding:5rem .85rem 3.5rem;}
  .hero h1{font-size:2.1rem;}
  .h-pill{font-size:.68rem;padding:.38rem .9rem;}
  .hero-badges{gap:.4rem;}
  .h-badge{font-size:.7rem;padding:.25rem .7rem;}
  .why-grid{grid-template-columns:1fr;}
  .vm-grid{grid-template-columns:1fr;}
  .orb-wrap{width:200px;height:200px;}
  .ghc-card{padding:1.5rem 1.2rem;}
  .stat-num{font-size:1.8rem;}
  .sec-title{font-size:1.7rem;}
  .innov-feat{padding:.9rem 1rem;}
  .tl-dot{width:40px;height:40px;font-size:.58rem;}
}
`;

/* ─── Floating particles component ──────────────────────────── */
function Particles({ count = 38, color = 'rgba(255,255,255,0.55)' }) {
  const canvasRef = useRef(null);
  const anim = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, pts;

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    pts = Array.from({ length: count }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .45, vy: (Math.random() - .5) * .45,
      r: Math.random() * 2 + 1,
      a: Math.random(),
    }));

    function draw() {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = color.replace('0.55', String(.2 + p.a * .45));
        ctx.fill();
      });
      // connect nearby
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y;
          const d = Math.sqrt(dx*dx+dy*dy);
          if (d < 110) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = color.replace('0.55', String(.06 * (1 - d / 110)));
            ctx.lineWidth = .8;
            ctx.stroke();
          }
        }
      }
      anim.current = requestAnimationFrame(draw);
    }
    draw();
    return () => {
      cancelAnimationFrame(anim.current);
      window.removeEventListener('resize', resize);
    };
  }, [count, color]);

  return <canvas ref={canvasRef} style={{ position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none',zIndex:1 }} />;
}

/* ─── Scroll reveal hook ─────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.rv');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ─── Number counter ─────────────────────────────────────────── */
function CountUp({ target, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const num = parseInt(target);
      const dur = 1200, steps = 40;
      let i = 0;
      const t = setInterval(() => {
        i++;
        setVal(Math.round(num * Math.min(i / steps, 1)));
        if (i >= steps) clearInterval(t);
      }, dur / steps);
    }, { threshold: .5 });
    io.observe(el);
    return () => io.disconnect();
  }, [target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ─── Main component ─────────────────────────────────────────── */
export default function Company() {
  useReveal();

  return (
    <>
      <style>{css}</style>

      {/* ══ HERO ══ */}
      <img src={CompanyImg} alt="Company" style={{ width: '100%', height: 'auto', display: 'block' }} />

      {/* ══ STATS ══ */}
      <div className="stats-wrap">
        <div className="stats-inner">
          {[
            { num:'500', suf:'+', label:'Products' },
            { num:'20',  suf:'+', label:'States Served' },
            { num:'15',  suf:'+', label:'Years Active' },
            { num:'6',   suf:'',  label:'Divisions' },
          ].map(s=>(
            <div key={s.label} className="stat-cell rv">
              <div className="stat-num"><CountUp target={s.num} suffix={s.suf}/></div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ ABOUT / WHO WE ARE ══ */}
      <section id="who-we-are" className="sec about-bg">
        <div className="deco-circle tl"/><div className="deco-circle br"/>
        <div className="sec-in">
          <div className="about-grid">

            {/* left */}
            <div className="rv">
              <div className="sec-tag">Our Story</div>
              <h2 className="sec-title">Committed to Better Healthcare</h2>
              <div className="about-text" style={{marginTop:'1.25rem'}}>
                <p>
                  We are a pharmaceutical marketing company committed to delivering high-quality, safe, and affordable healthcare products to everyone at the earliest. Our portfolio includes a wide range of pharmaceutical and nutraceutical products such as tablets, capsules, syrups, cosmetics, and other healthcare products.
                </p>
                <p>
                  We are driven by the vision to create a strong presence in the pharmaceutical industry through ethical practices, customer satisfaction, and continuous growth.
                </p>
              </div>
              <div className="vm-grid">
                {[
                  { label:'Vision',  icon:Eye,    text:'Provide high-quality medicine to everyone at affordable prices' },
                  { label:'Mission', icon:Target, text:'Deliver quality medicine to cities and rural areas through accessible distribution' },
                ].map(({ label, icon:Icon, text })=>(
                  <div key={label} className="vm-card">
                    <div className="vm-icon-wrap"><Icon size={16} color="#fff"/></div>
                    <h4>Our {label}</h4>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* right */}
            <div className="rv d2">
              <div className="ghc-card">
                <h3>Galaxy Health Care</h3>
                {['WHO-GMP Certified Partners','Drug License Compliant','GST Registered','500+ Product Portfolio','Pan-India Distribution','Quality First Approach'].map(item=>(
                  <div key={item} className="ghc-row">
                    <div className="ghc-dot"><CheckCircle size={12} color="#fff"/></div>
                    {item}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══ MILESTONES ══ */}
      <section className="sec milestones-bg">
        <div className="deco-grid"/>
        <div className="sec-in">
          <div style={{textAlign:'center',marginBottom:'3.5rem'}}>
            <div className="rv sec-tag">Our Journey</div>
            <h2 className="rv d1 sec-title">Key Milestones</h2>
            <p className="rv d2 sec-sub" style={{maxWidth:500,margin:'.75rem auto 0'}}>
              Fifteen years of steady, purposeful growth in Indian healthcare.
            </p>
          </div>

          <div className="tl-wrap">
            <div className="tl-spine"/>
            {milestones.map((m, i) => {
              const isLeft = i % 2 === 0;
              return (
                <div key={m.year} className={`rv d${(i%3)+1} tl-row ${isLeft ? 'tl-left' : 'tl-right'}`}>
                  {isLeft ? (
                    <>
                      <div className="tl-card"><p>{m.event}</p></div>
                      <div className="tl-dot">{m.year}</div>
                      <div className="tl-spacer"/>
                    </>
                  ) : (
                    <>
                      <div className="tl-spacer"/>
                      <div className="tl-dot">{m.year}</div>
                      <div className="tl-card"><p>{m.event}</p></div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ WHY US ══ */}
      <section id="why-us" className="sec why-bg">
        <div className="deco-circle tl" style={{opacity:.6}}/><div className="deco-circle br" style={{opacity:.5}}/>
        <div className="sec-in">
          <div style={{textAlign:'center'}}>
            <div className="rv sec-tag">Our Strengths</div>
            <h2 className="rv d1 sec-title">Why Choose Us?</h2>
            <p className="rv d2 sec-sub" style={{maxWidth:520,margin:'.75rem auto 0'}}>
              Eight reasons why healthcare providers, distributors, and pharmacies trust Bleen.
            </p>
          </div>
          <div className="why-grid">
            {whyUsPoints.map(({ icon:Icon, title, desc }, i)=>(
              <div key={title} className={`rv d${(i%4)+1} why-card`}>
                <div className="w-icon"><Icon size={20} color="#fff"/></div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ INNOVATION / BANYAN ══ */}
      <section id="innovation" className="sec innov-bg">
        <div className="innov-dots"/><div className="innov-blob1"/><div className="innov-blob2"/>
        <Particles count={28} color="rgba(46,180,180,0.55)" />
        <div className="sec-in">
          <div className="innov-grid">

            {/* left */}
            <div className="rv">
              <div className="sec-tag innov-tag">Innovation</div>
              <h2 className="sec-title innov-title">The Banyan Tree Growth Model</h2>
              <p className="innov-body" style={{marginTop:'1.25rem'}}>
                Innovation at our company is not only about pharmaceuticals and marketing strategies - it is also about long-term professional relationships and growth opportunities within our organization.
              </p>
              <p className="innov-body">
                Like a banyan tree that grows new branches to support it in the long run, our model encourages committed associates and business partners to grow along with our organization.
              </p>
              <div style={{marginTop:'1.75rem'}}>
                {[
                  { title:'Partner Growth Model',    desc:'Committed long-term associates get opportunities to lead new business branches.' },
                  { title:'Leadership Development',  desc:'Helping experienced professionals take on new leadership roles through our systems.' },
                  { title:'Mutual Success Strategy', desc:'Growth centered around mutual benefit - organization and associates grow together.' },
                ].map(({ title, desc })=>(
                  <div key={title} className="innov-feat">
                    <div className="i-dot"/>
                    <div>
                      <h4>{title}</h4>
                      <p>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* right — orb */}
            <div className="rv d3" style={{display:'flex',justifyContent:'center',alignItems:'center'}}>
              <div className="orb-wrap">
                {[
                  {s:'90%',c:'rgba(46,180,180,.12)',d:'28s',dir:'normal'},
                  {s:'68%',c:'rgba(23,79,154,.18)',d:'20s',dir:'reverse'},
                  {s:'48%',c:'rgba(46,180,180,.22)',d:'14s',dir:'normal'},
                ].map(({s,c,d,dir},i)=>(
                  <div key={i} className="orb-ring" style={{
                    width:s,height:s,borderColor:c,
                    animationDuration:d,animationDirection:dir,
                  }}/>
                ))}
                <div className="orb-glow"/>
                <div className="orb-center">
                  <Sprout size={52} color="#2eb4b4" strokeWidth={1.5}/>
                  <div style={{color:'#fff',fontWeight:900,fontSize:'1.05rem',marginTop:'.65rem',letterSpacing:'-.01em'}}>
                    Banyan Tree
                  </div>
                  <div style={{color:'rgba(46,180,180,.82)',fontSize:'.78rem',marginTop:'.2rem',fontWeight:500}}>
                    Growth Model
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}